import { Fragment, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgressWithLabel from '../components/CircularProgressWithLabel';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import { removeRoleFromUser, getUserWithRole } from '../_api';
import Alert from '@mui/material/Alert';
import { BEM_NAME_ELEMENTS } from '../_const';

const SummaryView = props => {
	const [sIsLoading, setIsLoading] = useState(true);
	const [sPrivilegedUsers, setPrivilegedUsers] = useState([]);
	const [sDeleteTargetUser, setDeleteTargetUser] = useState(false);
	const [sOpenDialog, setOpenDialog] = useState(false);
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);

	const handleClickOpenDialog = user => {
		setDeleteTargetUser(user);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setDeleteTargetUser(null);
		setOpenDialog(false);
	};

	const submitDelete = async () => {
		//prepare
		handleCloseDialog();
		setIsLoading(true);

		//delete
		const resultOfRemoval = await removeRoleFromUser(sDeleteTargetUser);
		setSubmissionStatus({
			message: resultOfRemoval
				? 'Successfully removed role from user'
				: 'Unable to remove role from user',
		});

		//refetch updated list
		const privilegedUsers = await getUserWithRole();
		setPrivilegedUsers(privilegedUsers);
		setIsLoading(false);
	};

	useEffect(() => {
		let active = true;

		async function fetchApi() {
			const privilegedUsers = await getUserWithRole();
			setPrivilegedUsers(privilegedUsers);
			setIsLoading(false);
		}

		if (active) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, []);

	return (
		<PageWithBreadcrumbs
			currentPageLabel='Privileged User Summary'
			isSearch
		>
			<Fragment key={props.key}>
				<Typography
					variant='h6'
					color='primary'
					align='center'
					gutterBottom
				>
					Epic Administration Privileged Users
				</Typography>
				<Typography variant='body1' align='center' gutterBottom>
					The users below have the necessary privileges to access the
					Epic Administration functionality.
				</Typography>
				{sSubmissionStatus?.message && (
					<Alert
						severity={
							sSubmissionStatus?.error ? 'error' : 'success'
						}
						variant='filled'
						onClose={() => {
							setSubmissionStatus(prev => (prev.message = ''));
						}}
					>
						{sSubmissionStatus?.message}
					</Alert>
				)}
				{sIsLoading ? (
					<Stack direction='column' alignItems='center' gap={1}>
						<CircularProgressWithLabel label='Loading...' />
					</Stack>
				) : sPrivilegedUsers?.length > 0 ? (
					<List>
						{sPrivilegedUsers.map(privilegedUsers => {
							return (
								<ListItem
									key={privilegedUsers.ID}
									secondaryAction={
										<IconButton
											id={`${BEM_NAME_ELEMENTS.VIEW_SECURITY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--remove-${privilegedUsers.ID}`}
											edge='end'
											aria-label='delete'
											onClick={() =>
												handleClickOpenDialog(
													privilegedUsers
												)
											}
										>
											<DeleteIcon />
										</IconButton>
									}
									disablePadding
								>
									<ListItemText
										id={`checkbox-list-label-${privilegedUsers?.ID}`}
										primary={`${privilegedUsers?.Name}`}
										secondary={`${privilegedUsers?.NetworkID}`}
									/>
								</ListItem>
							);
						})}
					</List>
				) : (
					<Typography variant='h6' color={'error'}>
						No Users currently have this privilege.
					</Typography>
				)}
			</Fragment>
			<Dialog
				open={sOpenDialog}
				onClose={handleCloseDialog}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-name'
			>
				<DialogTitle id='alert-dialog-title'>
					Remove Epic Administration Privileges?
				</DialogTitle>
				<DialogContent>
					<DialogContentText id='alert-dialog-name'>
						User: {sDeleteTargetUser?.Name}
					</DialogContentText>
					<DialogContentText id='alert-dialog-ntid'>
						NTID: {sDeleteTargetUser?.NetworkID}
					</DialogContentText>
					<DialogContentText id='alert-dialog-email'>
						Email: {sDeleteTargetUser?.Email}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						id={`${BEM_NAME_ELEMENTS.VIEW_SECURITY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--modal-continue`}
						onClick={submitDelete}
						color='error'
						variant='contained'
					>
						Continue
					</Button>
					<Button
						id={`${BEM_NAME_ELEMENTS.VIEW_SECURITY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--modal-cancel`}
						onClick={handleCloseDialog}
						autoFocus
						variant='contained'
					>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</PageWithBreadcrumbs>
	);
};

export default SummaryView;
